import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Division Schema - Zod definitions for division and related config
 */

// Tiebreaker configuration (nested in config)
export const TiebreakerConfigSchema = z.object({
  id: z.string(),
  maxPointsScored: z.number().nullable().default(null),
  maxPointDifferential: z.number().nullable().default(null),
  hidden: z.boolean().default(false),
});

export type TiebreakerConfig = z.infer<typeof TiebreakerConfigSchema>;

// Tiebreakers by sport. When adding a new sport, also register it in
// apps/admin/src/components/DataTable/TiebreakerModal.tsx:initialSportsData
// so the UI knows what tiebreakers to offer, and keep the SPORTS list in
// apps/api/api/scripts/20-unset-stamped-config-defaults.ts in sync. Keys here
// MUST match the sport ids used by `organization.sports[0]` (see CLAUDE.md
// "Sport is single-sourced") — Mongoose's strict mode silently drops anything
// not declared here, which is why pickleball saves were vanishing before.
//
// NO `.default([])` on any sport key (CLAUDE.md config rule): the old defaults
// became Mongoose defaults that stamped `tiebreakers: { basketball: [], ... }`
// onto every document at create, which getConfig read as a deliberate override
// shadowing the parent chain — so tournament/league tiebreakers never reached
// divisions (owner-reported 2026-08-20). `.optional()` alone is NOT enough:
// Mongoose also gives array paths an IMPLICIT `[]` default, pinned to
// `default: undefined` in apps/api/api/models/shared/index.ts. Unset must mean
// "inherit"; an empty array also means "inherit" (getConfig meaningful-guard).
export const TiebreakersSchema = z.object({
  basketball: z.array(TiebreakerConfigSchema).optional(),
  football: z.array(TiebreakerConfigSchema).optional(),
  baseball: z.array(TiebreakerConfigSchema).optional(),
  softball: z.array(TiebreakerConfigSchema).optional(),
  soccer: z.array(TiebreakerConfigSchema).optional(),
  pickleball: z.array(TiebreakerConfigSchema).optional(),
  golf: z.array(TiebreakerConfigSchema).optional(),
  volleyball: z.array(TiebreakerConfigSchema).optional(),
});

export type Tiebreakers = z.infer<typeof TiebreakersSchema>;

// Player eligibility sub-schemas. Live grouped under `config.eligibility`
// (e.g. `config.eligibility.age.minAge`) and resolve per-sub-key via the
// standard config hierarchy (division > league > tournament)
// so a division can override one constraint while inheriting the others.
export const RatingConstraintsSchema = z.object({
  minRating: z.number().optional(),
  maxRating: z.number().optional(),
});
export type RatingConstraints = z.infer<typeof RatingConstraintsSchema>;

export const AgeConstraintsSchema = z.object({
  minAge: z.number().optional(),
  maxAge: z.number().optional(),
});
export type AgeConstraints = z.infer<typeof AgeConstraintsSchema>;

export const EligibilityConstraintsSchema = z.object({
  gender: z.array(z.string()).optional(),
  rating: RatingConstraintsSchema.optional(),
  age: AgeConstraintsSchema.optional(),
});
export type EligibilityConstraints = z.infer<typeof EligibilityConstraintsSchema>;

// Shared config schema reused as `config` by Division, League, Tournament,
// and Registration. Every field resolves via the first-wins hierarchy:
// division > league > tournament. See
// packages/shared/utils/getConfig.ts. No per-field exceptions.
export const ConfigSchema = z.object({
  requirePlayers: z.boolean().optional(),
  tiebreakers: TiebreakersSchema.optional(),
  // Playoff-only tie rule (key name is LEGACY — kept for stored prod data).
  // When true, a playoff game that ends level (homeScore === awayScore, and not
  // 0–0) is awarded to the team placed HIGHER IN THE REGULAR-SEASON/POOL
  // STANDINGS: the public standings page highlights it as the winner and the
  // server's bracket advancement (apps/api/api/models/game/bracketAdvancement.ts)
  // feeds it into the next slot. The rank is computed by the SAME shared
  // function on both sides (packages/shared/utils/standings.ts) so the page and
  // the bracket can never disagree. The manually entered `divisionTeam.seed`
  // plays NO part in tie-breaking (stakeholder decision 2026-08-21 — stale
  // pre-tournament seeds must never overrule the pool result); a tie between
  // teams with no standings position stays undecided. REGULAR-SEASON ties are
  // never affected. Resolves through the standard hierarchy. Default off.
  // See ADR 0029.
  breakPlayoffTiesBySeed: z.boolean().optional(),
  hideTeams: z.boolean().optional(),
  // NO `.default(false)` — see the note above `capacity` below. A default here
  // becomes a Mongoose default, gets STAMPED on every child document, and
  // `getConfig` then reads that `false` as a deliberate override of the parent.
  hideSchedules: z.boolean().optional(),
  hideStandings: z.boolean().optional(), // Hide the public division Standings tab.
  // Whether stats are visible to the public (positive form). Resolves through the
  // hierarchy; unset falls through and consumers default to "show". (The legacy
  // !organization.hideStats fallback was retired — ADR 0035; script 25.)
  showStats: z.boolean().optional(),
  excludeTeamName: z.boolean().optional(),
  showAllTeams: z.boolean().optional(), // Show all teams in the division, even non active ones
  // EXCEPTION to the hierarchy rule above: read ONLY from `registration.config`
  // (set in the create-registration wizard), never resolved through getConfig.
  // Gates the public "Teams by Division" roster on /register/details. Default off.
  showAllRegistrationTeams: z.boolean().optional(),
  // EXCEPTION to the hierarchy rule (like showAllRegistrationTeams):
  // read ONLY from `registration.config`, never via getConfig. When true, the
  // registration is publicly viewable but users cannot register until
  // `registration.startDate` (date + time) has passed — gated by
  // isBeforeRegistrationStart (packages/shared/utils/registrationSchedule.ts).
  // Default off (registration open immediately).
  gateByStartDate: z.boolean().optional(),
  // EXCEPTION to the hierarchy rule (like gateByStartDate): read ONLY from
  // `registration.config`, never via getConfig. When true, the registration is
  // publicly viewable but users can no longer register once `registration.endDate`
  // (date + time, interpreted in `registration.timeZone`) has passed — gated by
  // isAfterRegistrationEnd (packages/shared/utils/registrationSchedule.ts).
  // Existing teams stay viewable/editable and "Teams by Division" still shows.
  // Default off (registration never auto-closes).
  gateByEndDate: z.boolean().optional(),
  // EXCEPTION to the hierarchy rule (like showAllRegistrationTeams /
  // gateByStartDate / gateByEndDate): read ONLY from `registration.config`,
  // never via getConfig. When true, the public registration details page
  // offers "Sign up as an individual" — a solo signup that lands in the
  // admin's free-agent pool for team assignment (ADR 0054). Default off.
  allowFreeAgents: z.boolean().optional(),
  minPlayers: z.number().optional(),
  maxPlayers: z.number().optional(),
  gameDurationInMinutes: z.number().optional(),
  requireCoaches: z.boolean().optional(),
  requireTeamRepresentatives: z.boolean().optional(),
  minTeamRepresentatives: z.number().optional(),
  maxTeamRepresentatives: z.number().optional(),
  minCoaches: z.number().optional(),
  maxCoaches: z.number().optional(),
  disableSync: z.boolean().optional(),
  // NEVER give a ConfigSchema field a `.default()`. This schema is shared by
  // Division, League, Tournament AND Registration, and `toMongooseSchema` turns
  // a Zod default into a Mongoose default — so the value is physically written
  // onto every child document at create time. `getConfig` treats anything that
  // isn't undefined/null as "deliberately set", so a stamped `false`/`0` on a
  // division silently shadows a real value on its parent and the hierarchy stops
  // working for that field. Unset must mean "inherit"; consumers supply the
  // fallback (`Number(x || 0)`, `!!x`, DEFAULT_PROMOTION_PAYMENT_WINDOW_HOURS).
  // See ADR 0032.
  capacity: z.number().optional(),
  hasWaitlist: z.boolean().optional(),
  autoPromote: z.boolean().optional(),
  promotionPaymentWindowHours: z.number().optional(),
  format: z.string().optional(),
  pools: z.array(z.object({
    id: z.number(),
    name: z.string(),
    teamIds: z.array(z.string()),
  })).optional(),
  roundsPerMatchup: z.number().min(1).max(8).optional(),
  // Playoff bracket size for playoff formats (round-robin-playoffs, pool-play-playoffs,
  // bracket, playoffs): 2 (Final only) | 4 (Semifinals + 3rd place) | 8 (Quarterfinals).
  // Read directly off division.config (like pools/roundsPerMatchup), NOT via getConfig.
  // Unset falls back to defaultPlayoffTeams(format) (8 for bracket/playoffs, else 4).
  playoffTeams: z.number().optional(),
  playerAutoComplete: z.boolean().optional(),
  // Single source of truth for which stat columns to show, resolved through the
  // config hierarchy (division > league > tournament). Holds the RAW per-game stat
  // keys (used for box scores / game logs / the admin entry grid); the public
  // AGGREGATE columns are DERIVED from this (avg of each + always gameCount) — see
  // inferDisplayStats (packages/shared/utils/inferDisplayStats.ts). Sport-keyed so a
  // key like `points`/`blocks` is unambiguous across sports:
  //   { basketball: ['points','totalRebounds'], volleyball: ['points','blocks'] }
  // The consumer picks the entity's sport (organization.sports[0]) and falls back to
  // the legacy flat `organization.gameStats` when unset.
  gameStats: z.record(z.array(z.string())).optional(),
  // How public AGGREGATE player/team stat columns render: 'average' (per game,
  // the default) or 'total' (cumulative season totals). Resolves through the config
  // hierarchy. Only affects aggregate stat tables (player/team stat pages,
  // leaderboard) — raw per-game box scores are unchanged. Consumed via
  // getStatDisplayMode → statParser.playerCummulativeStats.
  statDisplayMode: z.enum(['average', 'total']).optional(),
  // Player eligibility — allowed genders + rating/age thresholds. Cascades
  // per-sub-key via getConfig (division > league > tournament),
  // so a division can override `gender` while still inheriting `age` / `rating`.
  eligibility: EligibilityConstraintsSchema.optional(),
  // Registration fee / donation product refs (PaymentProduct), resolved through
  // the standard hierarchy like every other config field (ADR 0034 — these used
  // to be TOP-LEVEL entity fields with hand-rolled fallback chains ending at the
  // registration). NO defaults, per the stamping rule above. The Mongoose refs
  // come from `configMetadata` below (ConfigFields is generated with it). On
  // Registration these two keys are physically present (shared schema) but
  // never read.
  paymentProduct: objectId,
  donationProduct: objectId,
});

export type Config = z.infer<typeof ConfigSchema>;

// Division schema
export const DivisionSchema = z.object({
  _id: objectId,
  name: z.string(),
  organization: objectId,
  league: objectId,
  sports: z.array(z.string()).default([]),
  priority: z.number().optional(),
  // Top-level paymentProduct was REMOVED (ADR 0034) — the fee now lives at
  // `config.paymentProduct` and resolves through the hierarchy. Old documents'
  // stored top-level values are inert fossils (strict hydration hides them);
  // migrated by api/scripts/24-unify-payment-into-config.ts.
  admins: z.array(objectId).default([]),
  config: ConfigSchema.optional(),
  strengthLevel: z.number().optional(),
  startDate: z.coerce.date().default(() => new Date()),
  endDate: z.coerce.date().default(() => new Date()),
  ...timestampFields,
});

export type Division = z.infer<typeof DivisionSchema>;

// Metadata for Division fields (for Mongoose refs and UI)
export const divisionMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  name: { label: 'Division Name', editable: true },
  organization: { ref: 'Organization', hidden: true },
  league: { ref: 'League', hidden: true },
  sports: { label: 'Sports', editable: true },
  priority: { label: 'Priority', editable: true },
  admins: { ref: 'Admin', label: 'Admins' },
  strengthLevel: { label: 'Strength Level', editable: true },
  startDate: { label: 'Start Date', editable: true },
  endDate: { label: 'End Date', editable: true },
  created: { hidden: true },
  updated: { hidden: true },
};

// Metadata for Config fields. ConfigFields (apps/api/api/models/shared) is
// generated from ConfigSchema WITH this metadata, so refs declared here become
// real Mongoose ObjectId refs on `config.*` for every model that embeds it.
export const configMetadata: Record<string, FieldMetadata> = {
  paymentProduct: { ref: 'PaymentProduct', label: 'Payment Product' },
  donationProduct: { ref: 'PaymentProduct', label: 'Donation Product' },
};
